import { Playbook } from '@chilipiper/api-client/src/chat-platform-builder'
import { LocationConditionEvaluationTree } from '@chilipiper/api-client/src/chat-platform-chat'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { SetAttendee } from '@chilipiper/api-client/src/meetings'
import { UpdateMeetingRequest } from '@chilipiper/api-client/src/meetings-api'
import {
  AvailabilityFeature,
  ReserveTimeSlotRequest,
} from '@chilipiper/api-client/src/user-availability'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { ApiError } from '@chilipiper/service/src/types'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import invariant from 'tiny-invariant'

export const useLocationSettings = ({
  workspaceId,
  tenantId,
  guestId,
}: {
  guestId?: string
  tenantId?: string
  workspaceId?: string
}) => {
  const { chatPlatformChat } = useApiClient()
  const query = useQuery({
    queryKey: ['location-settings', workspaceId, tenantId, guestId],
    queryFn: () => {
      invariant(
        workspaceId && tenantId && guestId,
        'workspaceId, tenantId and guestId are required'
      )
      return chatPlatformChat.getApiV1PublicTenantTenantidWorkspacesWorkspaceidLocationSettings(
        tenantId,
        workspaceId,
        {
          guestId,
        }
      )
    },
    enabled: Boolean(workspaceId && tenantId && guestId),
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as unknown as ApiError)
    }
  }, [query.error])

  return query
}

export const usePlaybooksSelectWithConversation = (tenantId: string) => {
  const { chatPlatformChat } = useApiClient()
  return useMutation({
    mutationFn: ({
      guestId,
      playbookIds,
      workspaceId,
      targetedUrl,
      forceStartNextSession,
      locationEvaluation,
    }: {
      guestId: string
      playbookIds: string[]
      workspaceId: string
      targetedUrl?: string
      forceStartNextSession?: boolean
      locationEvaluation?: Record<string, LocationConditionEvaluationTree>
    }) =>
      chatPlatformChat.putApiV1PublicTenantTenantidWorkspacesWorkspaceidPlaybooksSelectWithConversation(
        tenantId,
        workspaceId,
        {
          guestId,
          playbookIds,
          targetedUrl,
          locationEvaluation,
        },
        {
          forceStartNextSession,
        }
      ),
    onError: error => {
      defaultErrorCallback(error as unknown as ApiError)
    },
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export const usePrepareChat = (tenantId: string) => {
  const { chatPlatformChat } = useApiClient()
  return useMutation({
    mutationFn: ({ workspaceId, guestId }: { guestId: string; workspaceId: string }) =>
      chatPlatformChat.putApiV1PublicTenantTenantidWorkspacesWorkspaceidPrepareChat(
        tenantId,
        workspaceId,
        {
          guestId,
        }
      ),
    onError: error => {
      defaultErrorCallback(error as unknown as ApiError)
    },
  })
}

export const publishedPlaybookVersionKeys = (
  tenantId: string,
  workspaceId: string,
  playbookId: string
) => ['published-versions-playbook', tenantId, workspaceId, playbookId]

export const usePublishedPlaybookVersions = (
  workspaceId: string,
  playbookId: string,
  isEnabled = true
) => {
  const { tenantId } = useFireSessionContext()
  const { chatPlatformBuilder } = useApiClient()
  return useQuery<Playbook[]>({
    queryKey: publishedPlaybookVersionKeys(tenantId, workspaceId, playbookId),
    queryFn: () =>
      chatPlatformBuilder.getApiV1TenantTenantidWorkspacesWorkspaceidPlaybooksPublishedPlaybookid(
        tenantId,
        workspaceId,
        playbookId
      ),
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  })
}

export const useMeetingQueryOptions = () => {
  const { meetings } = useApiClient()
  return (tenantId: string, meetingId: string) => {
    return queryOptions({
      queryKey: ['meeting', tenantId, meetingId],
      queryFn: () => meetings.getAPublicMeeting(tenantId, meetingId),
      enabled: Boolean(tenantId && meetingId),
    })
  }
}

export const useMeeting = (tenantId: string, meetingId?: string) => {
  const meetingQueryOptions = useMeetingQueryOptions()

  const query = useQuery({
    ...meetingQueryOptions(tenantId, meetingId as string),
    retry: 5,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as unknown as ApiError)
    }
  }, [query.error])

  return query
}

export const useUpdateMeeting = (tenantId: string, meetingId: string) => {
  const { meetingsApi } = useApiClient()
  const queryClient = useQueryClient()
  const meetingQueryOptions = useMeetingQueryOptions()
  return useMutation({
    mutationFn: (payload: UpdateMeetingRequest) =>
      meetingsApi.updateAMeetingGuestEndpoint(tenantId, meetingId, payload),
    onError: error => {
      defaultErrorCallback(error as unknown as ApiError)
    },
    onSettled: async response => {
      const queryKey = meetingQueryOptions(tenantId, meetingId ?? '').queryKey
      if (response?.type === 'Accepted') {
        await queryClient.cancelQueries({
          queryKey,
        })
        queryClient.setQueryData(queryKey, previousData => {
          if (previousData) {
            const newAttendees: SetAttendee = [
              ...(previousData.attendees?.filter(
                attendee => attendee.value !== previousData.hostId
              ) ?? []),
              {
                type: 'User',
                value: response.assigneeId ?? previousData.hostId,
                responseStatus: 'NeedsAction',
                optional: false,
              },
            ]
            return {
              ...previousData,
              hostId: response.assigneeId ?? previousData.hostId,
              attendees: newAttendees,
            }
          }
        })
      } else {
        await queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

export const useReserveTimeSlot = (tenantId: string) => {
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: (
      payload: ReserveTimeSlotRequest & {
        feature?: AvailabilityFeature[]
      }
    ) => {
      const { feature, ...rest } = payload
      return userAvailability.postApiV1PublicTenantTenantidUsersSlots(tenantId, rest, { feature })
    },
    onError: error => {
      defaultErrorCallback(error as unknown as ApiError)
    },
  })
}

export const useChangeRoomStatus = (
  tenantId: string,
  roomId: string,
  { onError, onSuccess }: { onSuccess: (status: 'OPEN' | 'CLOSED') => void; onError: () => void }
) => {
  const { chatPlatformChat } = useApiClient()
  const { mutate, isPending: isLoading } = useMutation({
    mutationKey: ['chat-components', 'room-status', tenantId, roomId, 'mutation'],
    mutationFn: async (status: 'OPEN' | 'CLOSED') => {
      await chatPlatformChat.putApiV1TenantTenantidInboxManage(tenantId, {
        guestId: roomId,
        type: status === 'OPEN' ? 'OpenAllRelatedInboxes' : 'CloseAllRelatedInboxes',
      })
    },
  })
  return {
    isLoading,
    setRoomStatus: useCallback(
      (status: 'OPEN' | 'CLOSED') => {
        mutate(status, {
          onSuccess: () => {
            onSuccess(status)
          },
          onError,
        })
      },
      [mutate, onSuccess, onError]
    ),
  }
}
